import React from 'react';
import { Card, Table, Avatar, Typography, Tag, Progress } from 'antd';
import { TrophyOutlined, CrownOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  .rank-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .rank-number {
    font-weight: 700;
    font-size: 16px;
    min-width: 30px;

    &.top-1 { color: #faad14; }
    &.top-2 { color: #8c8c8c; }
    &.top-3 { color: #cd7f32; }
  }
  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .current-user-row {
    background-color: #e6f7ff !important;
  }
  .accuracy-badge {
    &.high { color: #52c41a; }
    &.medium { color: #faad14; }
    &.low { color: #ff4d4f; }
  }
`;

interface LeaderboardEntry {
  rank: number;
  user_id: string;
  name: string;
  corrections_count: number;
  reports_submitted: number;
  accuracy: number;
  is_current_user: boolean;
}

interface ErrorCorrectionLeaderboardProps {
  leaderboard: LeaderboardEntry[];
  currentUser: {
    rank: number;
    corrections_count: number;
    reports_submitted: number;
    accuracy: number;
  };
  loading?: boolean;
}

const getAccuracyColor = (accuracy: number) => {
  if (accuracy >= 80) return 'high';
  if (accuracy >= 50) return 'medium';
  return 'low';
};

const ErrorCorrectionLeaderboard: React.FC<ErrorCorrectionLeaderboardProps> = ({
  leaderboard,
  currentUser,
  loading = false
}) => {
  const columns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <div className="rank-cell">
          <span className={`rank-number ${rank <= 3 ? `top-${rank}` : ''}`}>
            #{rank}
          </span>
          {rank === 1 && <CrownOutlined style={{ color: '#faad14', fontSize: 20 }} />}
        </div>
      )
    },
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: LeaderboardEntry) => (
        <div className="user-info">
          <Avatar icon={<UserOutlined />} />
          <div>
            <Text strong={record.is_current_user}>
              {name || 'Anonymous'}
              {record.is_current_user && (
                <Tag color="blue" style={{ marginLeft: 8 }}>You</Tag>
              )}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Corrections',
      dataIndex: 'corrections_count',
      key: 'corrections_count',
      align: 'center' as 'center',
      render: (count: number) => (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          {count}
        </Tag>
      )
    },
    {
      title: 'Reports',
      dataIndex: 'reports_submitted',
      key: 'reports_submitted',
      align: 'center' as 'center',
      render: (count: number) => <Text>{count}</Text>
    },
    {
      title: 'Accuracy',
      dataIndex: 'accuracy',
      key: 'accuracy',
      align: 'center' as 'center',
      render: (accuracy: number) => (
        <div style={{ minWidth: 100 }}>
          <Progress
            percent={accuracy}
            size="small"
            strokeColor={
              accuracy >= 80 ? '#52c41a' :
              accuracy >= 50 ? '#faad14' : '#ff4d4f'
            }
            format={(percent) => `${percent}%`}
          />
        </div>
      )
    }
  ];

  return (
    <StyledCard
      bordered={false}
      loading={loading}
      title={
        <Title level={4} style={{ margin: 0 }}>
          <TrophyOutlined /> Error Correction Leaderboard
        </Title>
      }
    >
      <div style={{ marginBottom: 16, padding: 16, background: '#f0f0f0', borderRadius: 8 }}>
        <Text strong>Your Stats:</Text>
        <div style={{ marginTop: 8 }}>
          <div>Rank: <Text strong>#{currentUser.rank}</Text></div>
          <div>Approved Corrections: <Text strong>{currentUser.corrections_count}</Text></div>
          <div>Total Reports: <Text strong>{currentUser.reports_submitted}</Text></div>
          <div>
            Accuracy: <Text strong className={`accuracy-badge ${getAccuracyColor(currentUser.accuracy)}`}>
              {currentUser.accuracy}%
            </Text>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={leaderboard}
        rowKey="user_id"
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowClassName={(record) => record.is_current_user ? 'current-user-row' : ''}
      />

      <div style={{ marginTop: 16, padding: 12, background: '#e6f7ff', borderRadius: 4, fontSize: 12 }}>
        <strong>How to earn points:</strong>
        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
          <li>Submit error reports for species data</li>
          <li>Earn <strong>50 points</strong> for each approved correction</li>
          <li>Maintain high accuracy (80%+) to unlock bonus achievements</li>
          <li>Rise through the ranks to become a Quality Guardian!</li>
        </ul>
      </div>
    </StyledCard>
  );
};

export default ErrorCorrectionLeaderboard;
